import React, { useState, ChangeEvent, useEffect, FormEvent } from 'react';
import { makeStyles, TextField, Grid, Button, InputLabel, Theme, Checkbox, Select, MenuItem } from '@material-ui/core';
import { useAppState } from '../../../state';
import clsx from 'clsx';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TermsAndConditionsDialog from './TermsAndConditionsDialog/TermsAndConditionsDialog';
import cleanSitecodeString from '../../../utils/cleanSitecodeString';
import { validateAUPhone, validateBasicPhone } from '../../../utils/formatPhoneE164';
import { postCodeRegex } from '../../../constants/regex';
import { CATEGORIES_LIST } from '../../../constants/categories';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '1em 0 2em',
    '& div:not(:last-child)': {
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '1em 0 1.5em',
    },
  },
  flexContainer: {
    flexDirection: 'column',
  },
  textFieldContainer: {
    width: '100%',
  },
  textFieldContainerHalf: {
    width: '50%',
  },
  continueButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  errorMessage: {
    color: 'red',
    display: 'flex',
    alignItems: 'center',
    margin: '1em 0 0.2em',
    '& svg': {
      marginRight: '0.4em',
    },
  },
}));

interface RoomNameScreenProps {
  name: string;
  roomName: string;
  setName: (name: string) => void;
  setRoomName: (roomName: string) => void;
  email: string;
  siteCode: string;
  siteCodeError: string;
  phonenumber: string;
  disableSitecode: boolean;
  lastName: string;
  termsAndConditions: boolean;
  category: string;
  setCategory: (category: string) => void;
  //termsAndConditionsTop: boolean;
  setEmail: (email: string) => void;
  setPhoneNumber: (phonenumber: string) => void;
  setLastName: (lastName: string) => void;
  setSiteCode: (siteCode: string) => void;
  setTermsAndConditions: (termsAndConditions: boolean) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement> | null) => void;
  otpEnabled: boolean;
}

export default function RoomNameScreen({
  name,
  setName,
  siteCode,
  siteCodeError,
  phonenumber,
  disableSitecode,
  lastName,
  termsAndConditions,
  category,
  setCategory,
  setPhoneNumber,
  setLastName,
  setSiteCode,
  setTermsAndConditions,
  handleSubmit,
  otpEnabled,
}: RoomNameScreenProps) {
  const classes = useStyles();
  const { user } = useAppState();
  const [showRoomName, setShowRoomName] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  function checkBeforeSubmit() {
    const cleanedSitecode = cleanSitecodeString(siteCode);
    if (cleanedSitecode !== siteCode) {
      setSiteCode(cleanedSitecode);
    }

    const isPostcode = cleanedSitecode.match(postCodeRegex) !== null;
    const isCCAS = name
      .trim()
      .toUpperCase()
      .includes('CCAS');
    const needsStrictValidation = isPostcode && otpEnabled && !isCCAS;

    const isValid = needsStrictValidation ? validateAUPhone(phonenumber).valid : validateBasicPhone(phonenumber);

    if (!isValid) {
      setPhoneError(
        needsStrictValidation
          ? 'Please enter a valid Australian mobile or landline number'
          : 'Please enter a valid phone number (min. 8 digits)'
      );
    } else if (!isPostcode) {
      setPhoneError('');
      handleSubmit(null);
    } else {
      setPhoneError('');
      setShowConfirmDialog(true);
    }
  }

  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };

  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  };

  const handleSiteCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSiteCode(event.target.value);
    setCanSubmit(event.target.value.length > 0);
  };

  const handleTermsAndConditionsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTermsAndConditions(event.target.checked);
  };

  const handleCategoryChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCategory(event.target.value as string);
  };

  const hasUsername = !window.location.search.includes('customIdentity=true') && user?.displayName;

  useEffect(() => {
    const searchStr = window.sessionStorage.getItem('searchStr');
    if (!!searchStr) {
      const searchParams = new URLSearchParams(searchStr);
      const task = searchParams.get('task');
      if (!!task) {
        if (task === 'no') {
          setSiteCode('');
        }
        setShowRoomName(task !== 'no');
        setCanSubmit(task === 'no');
      } else {
        setShowRoomName(true);
        // setCanSubmit(!!workflowId);
      }
    } else {
      setShowRoomName(true);
      setCanSubmit(siteCode?.length > 0);
    }
  }, [siteCode, setSiteCode, canSubmit]);
  useEffect(() => {
    setCanSubmit(!!(name && lastName && phonenumber && termsAndConditions));
  }, [name, lastName, phonenumber, termsAndConditions]);

  useEffect(() => {
    const fetchSiteCode = () => {
      const url = window.location.href;
      const params = new URLSearchParams(new URL(url).search);
      const siteCodeValue = params.get('sitecode');
      if (siteCodeValue) {
        setSiteCode(siteCodeValue);
      }
    };
    const timeout = setTimeout(fetchSiteCode, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [setSiteCode]);

  return (
    <>
      <TermsAndConditionsDialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
        }}
        onResult={(acceptTermsAndConditions: boolean) => {
          if (acceptTermsAndConditions) {
            handleSubmit(null);
          } else {
            // do nothing
            setShowConfirmDialog(false);
          }
        }}
      />
      <form>
        <div className={classes.inputContainer}>
          <div className={classes.textFieldContainer} style={{ display: showRoomName ? 'inline' : 'none' }}>
            <InputLabel shrink htmlFor="input-site-code">
              Postcode or Site code <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <TextField
              autoCapitalize="false"
              id="input-site-code"
              variant="outlined"
              fullWidth
              size="small"
              value={siteCode}
              onChange={handleSiteCodeChange}
              disabled={disableSitecode}
              helperText={siteCodeError || 'Enter 4-digit postcode or clinic sitecode'}
              error={!!siteCodeError}
              FormHelperTextProps={{ style: { minHeight: '1.2em' } }}
            />
          </div>
          <div></div>
        </div>

        <div className={clsx(classes.inputContainer, classes.flexContainer)}>
          <div className={classes.textFieldContainer}>
            <InputLabel shrink htmlFor="input-phone-number">
              Contact number <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <TextField
              autoCapitalize="false"
              id="input-phone-number"
              variant="outlined"
              fullWidth
              size="small"
              value={phonenumber}
              onChange={handlePhoneNumberChange}
              helperText={phoneError || 'e.g. 0412345678 or (02) 1234 5678'}
              error={!!phoneError}
              FormHelperTextProps={{ style: { minHeight: '1.2em' } }}
            />
          </div>
          <div></div>
        </div>

        <div className={classes.inputContainer}>
          {!hasUsername && (
            <div className={classes.textFieldContainer}>
              <InputLabel shrink htmlFor="input-user-name">
                First Name <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                id="input-user-name"
                variant="outlined"
                fullWidth
                size="small"
                value={name}
                onChange={handleNameChange}
              />
            </div>
          )}
          <div className={classes.textFieldContainer}>
            <InputLabel shrink htmlFor="input-last-name">
              Last Name <span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <TextField
              autoCapitalize="false"
              id="input-last-name"
              variant="outlined"
              fullWidth
              size="small"
              value={lastName}
              onChange={handleLastNameChange}
            />
          </div>
        </div>
        <div className={classes.inputContainer}>
          <div className={classes.textFieldContainer}>
            <InputLabel shrink id="label-category" htmlFor="input-category">
              Category (optional - only for hospital site usage)
            </InputLabel>
            <div className={classes.textFieldContainerHalf}>
              <Select
                fullWidth
                labelId="label-category"
                id="input-category"
                value={category}
                variant="outlined"
                onChange={handleCategoryChange}
              >
                {CATEGORIES_LIST.map(categoryItem => (
                  <MenuItem key={categoryItem.value} value={categoryItem.value}>
                    {categoryItem.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <div className={classes.inputContainer}>
          <div className={classes.textFieldContainer}>
            <FormControlLabel
              label={
                <div>
                  <span>I agree with the </span>
                  <a
                    href="https://www.myemergencydr.com/service-terms-and-conditions/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    terms and conditions
                  </a>
                  <span> and </span>
                  <a href="https://www.myemergencydr.com/privacy-policy/" target="_blank" rel="noreferrer">
                    privacy policy
                  </a>
                  <span> of this site </span>
                  <span style={{ color: 'red' }}>*</span>
                </div>
              }
              control={
                <Checkbox
                  autoCapitalize="false"
                  id="input-terms-and-conditions"
                  size="small"
                  checked={termsAndConditions}
                  onChange={handleTermsAndConditionsChange}
                />
              }
            />
          </div>
        </div>

        <Grid container justifyContent="flex-end">
          <Button
            variant="contained"
            type="button"
            color="primary"
            disabled={!name || !lastName || !phonenumber || !termsAndConditions || !canSubmit}
            className={classes.continueButton}
            onClick={checkBeforeSubmit}
          >
            Continue
          </Button>
        </Grid>
      </form>
    </>
  );
}
